import { useState, useMemo } from 'react';
import { Box, ListItem, Typography, MenuItem, Chip, Menu, Popover, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { format } from 'date-fns';

import { colors } from '@src/styles';
import { statusColors, statusIcons } from './StatusBar';
import { TTaskItem } from '../hooks/dto/taskItem.dto';
import { useTasksContext } from '../context/TasksContext';

interface TaskRowProps {
  task: TTaskItem;
}

export const TaskRow = ({ task }: TaskRowProps) => {
  const {
    userNames,
    organizationNames,
    teamMembers,
    handleTaskClick,
    handleUpdateTaskStatus,
    handleUpdateTaskUser,
    selectedTaskId,
  } = useTasksContext();

  const [statusMenuAnchorEl, setStatusMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [isClicked, setIsClicked] = useState(false);
  const [hoveredStatus, setHoveredStatus] = useState<TTaskItem['status'] | null>(null);
  const [userSearch, setUserSearch] = useState('');

  const isSelected = task.id === selectedTaskId;
  const filteredUserList = useMemo(() => {
    const list = teamMembers.map((member) => ({ id: member.id, name: member.name }));
    const filtered = userSearch ? list.filter((u) => u.name.toLowerCase().includes(userSearch.toLowerCase())) : list;
    return filtered;
  }, [teamMembers, userSearch]);
  const StatusIcon = statusIcons[task.status || 'PENDING'];

  const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setStatusMenuAnchorEl(event.currentTarget);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchorEl(null);
  };

  const handleStatusChange = (status: TTaskItem['status']) => {
    handleUpdateTaskStatus(task.id, status);
    handleStatusMenuClose();
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setUserMenuAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchorEl(null);
    setUserSearch('');
  };

  const handleUserChange = (userId: string | null) => {
    handleUpdateTaskUser(task.id, userId);
    handleUserMenuClose();
  };

  const onTaskClick = () => {
    if (isClicked) return;
    setIsClicked(true);
    handleTaskClick(task);
    setTimeout(() => setIsClicked(false), 500);
  };

  return (
    <ListItem
      onClick={onTaskClick}
      sx={{
        padding: '5px 12px',
        borderRadius: '10px',
        backgroundColor: isSelected ? 'rgba(255, 160, 21, 0.07)' : colors.brand.surface,
        marginBottom: 0.5,
        border: `1px solid ${isSelected ? 'rgba(255, 160, 21, 0.45)' : colors.brand.line}`,
        borderLeft: `2px solid ${isSelected ? colors.primary.yellow : 'transparent'}`,
        transition: 'border-color 0.2s ease, background-color 0.2s ease',
        '&:hover': {
          borderColor: colors.primary.yellow,
          cursor: 'pointer',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
        <Box sx={{ display: 'flex', width: '200px', alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              color: colors.primary.white,
              fontSize: '12px',
              fontFamily: 'monospace',
              width: '90%',
              opacity: 0.8,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {organizationNames[task.organizationId] || task.organizationId || '-'}
          </Typography>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              color: colors.primary.white,
              fontWeight: 500,
              fontFamily: 'inter',
              fontSize: '13px',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {task.message}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', width: '80px', alignItems: 'center', justifyContent: 'flex-start' }}>
          <Box
            onClick={handleStatusMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '26px',
              height: '26px',
              borderRadius: '50%',
              backgroundColor: `${statusColors[task.status || 'PENDING']}20`,
              border: `2px solid ${statusColors[task.status || 'PENDING']}`,
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              '&:hover': {
                backgroundColor: `${statusColors[task.status || 'PENDING']}40`,
              },
            }}
          >
            <StatusIcon
              sx={{
                color: statusColors[task.status || 'PENDING'],
                fontSize: '15px',
              }}
            />
          </Box>
          <Popover
            anchorEl={statusMenuAnchorEl}
            open={Boolean(statusMenuAnchorEl)}
            onClose={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              handleStatusMenuClose();
              setHoveredStatus(null);
            }}
            onClick={(e) => e.stopPropagation()}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transitionDuration={20}
            slotProps={{
              paper: {
                sx: {
                  backgroundColor: colors.background.primary,
                  borderRadius: '20px',
                  padding: '6px 10px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                  border: `1px solid ${colors.background.secondary}`,
                  marginTop: '6px',
                  overflow: 'visible',
                },
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                alignItems: 'center',
                height: '27px',
              }}
            >
              {['PENDING', 'IN_PROGRESS', 'CONCLUDED', 'REJECTED'].map((status) => {
                const Icon = statusIcons[status];
                const isHovered = hoveredStatus === status;
                const isCurrent = task.status === status;
                return (
                  <Box
                    key={status}
                    onClick={() => handleStatusChange(status as TTaskItem['status'])}
                    onMouseEnter={() => setHoveredStatus(status as TTaskItem['status'])}
                    onMouseLeave={() => setHoveredStatus(null)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '27px',
                      height: '27px',
                      borderRadius: '50%',
                      backgroundColor:
                        isHovered || isCurrent ? `${statusColors[status]}30` : `${statusColors[status]}10`,
                      border: `2px solid ${isHovered || isCurrent ? statusColors[status] : 'transparent'}`,
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <Icon
                      sx={{
                        color: statusColors[status],
                        fontSize: '15px',
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
          </Popover>
        </Box>

        <Box sx={{ display: 'flex', width: '140px', alignItems: 'center' }}>
          <Typography variant="body2" sx={{ color: colors.primary.white, fontSize: '12px', fontFamily: 'archivo' }}>
            {format(new Date(task.createdAt), 'MMM dd, HH:mm')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', width: '120px', alignItems: 'center' }}>
          <Chip
            label={task.source}
            size="small"
            sx={{
              backgroundColor: colors.background.primary,
              color: colors.primary.white,
              fontSize: '10.5px',
              fontWeight: 500,
              border: `1px solid ${colors.background.secondary}`,
              height: '22px',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', width: '100px', alignItems: 'center' }}>
          <Chip
            label={task.type}
            size="small"
            sx={{
              backgroundColor: colors.background.primary,
              color: colors.primary.white,
              fontSize: '10.5px',
              fontWeight: 500,
              border: `1px solid ${colors.background.secondary}`,
              height: '22px',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', width: '140px', alignItems: 'center' }}>
          <Chip
            label={task.assignedUserId ? userNames[task.assignedUserId] : 'Não atribuído'}
            onClick={handleUserMenuOpen}
            size="small"
            sx={{
              width: '100%',
              height: '26px',
              cursor: 'pointer',
              '& .MuiChip-label': { fontSize: '11.5px', fontWeight: 600 },
            }}
          />
          <Menu
            anchorEl={userMenuAnchorEl}
            open={Boolean(userMenuAnchorEl)}
            onClose={(e: React.MouseEvent<HTMLElement>) => {
              e.stopPropagation();
              handleUserMenuClose();
            }}
            onClick={(e) => e.stopPropagation()}
            transitionDuration={0}
            slotProps={{
              paper: {
                sx: {
                  maxHeight: 300,
                  backgroundColor: colors.background.primary,
                  border: `1px solid ${colors.background.secondary}`,
                  overflow: 'hidden',
                },
              },
            }}
          >
            <Box
              sx={{ px: 1, pb: 1, position: 'sticky', top: 0, backgroundColor: colors.background.primary, zIndex: 1 }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <TextField
                size="small"
                placeholder="Buscar..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                autoFocus
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: '18px', color: colors.primary.white, opacity: 0.5 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: colors.primary.white,
                    fontSize: '13px',
                    backgroundColor: colors.background.secondary,
                    borderRadius: '6px',
                    '& fieldset': { borderColor: 'transparent' },
                    '&:hover fieldset': { borderColor: colors.primary.yellow },
                    '&.Mui-focused fieldset': { borderColor: colors.primary.yellow },
                  },
                  '& .MuiInputBase-input::placeholder': { color: colors.primary.white, opacity: 0.5 },
                }}
              />
            </Box>
            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
              <MenuItem onClick={() => handleUserChange(null)} sx={{ color: colors.primary.white, fontSize: '13px' }}>
                <em>Não atribuído</em>
              </MenuItem>
              {filteredUserList.map((user) => (
                <MenuItem
                  key={user.id}
                  onClick={() => handleUserChange(user.id)}
                  sx={{ color: colors.primary.white, fontSize: '13px' }}
                >
                  {user.name}
                </MenuItem>
              ))}
              {filteredUserList.length === 0 && userSearch && (
                <Typography
                  variant="body2"
                  sx={{ px: 2, py: 1, color: colors.primary.white, opacity: 0.5, fontSize: '13px' }}
                >
                  Nenhum resultado
                </Typography>
              )}
            </Box>
          </Menu>
        </Box>
      </Box>
    </ListItem>
  );
};
